"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent, RefObject } from "react";
import { createPortal } from "react-dom";
import { Button } from "@xefi/x-react/button";
import { Input, Switch, Textarea } from "@xefi/x-react/form";
import { IconCheck, IconX } from "@xefi/x-react/icons";
import { addToast } from "@xefi/x-react/toast";
import { useTranslation } from "react-i18next";
import type { MissingCardPaymentModel, MissingCardPaymentMutableAttributes } from "../../../../domain/models/MissingCardPayment";
import { usePermissions } from "@/hooks/usePermissions";
import { useUpdateMissingCardPayment } from "@/hooks/useUpdateMissingCardPayment";
import { getEditErrorMessage } from "../common/editError";
import { isDone } from "../common/paymentsFormat";
export type EditableField = "assignment" | "to_do" | "comment";
type Coordinates = {
  top: number;
  left: number;
};
type Props = {
  row: MissingCardPaymentModel;
  field: EditableField;
  anchorRef: RefObject<Element | null>;
  onClose: () => void;
};
const PANEL_MARGIN = 6;
export const MissingCardPaymentRowEditor = t0 => {
  const $ = _c(104);
  const {
    row,
    field,
    anchorRef,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    hasRole
  } = usePermissions();
  let t1;
  if ($[0] !== t) {
    t1 = name => t(`paymentsReceived.missingCardPayments.columns.${name}`);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  const isToggle = field === "to_do";
  let t2;
  if ($[2] !== hasRole || $[3] !== isToggle) {
    t2 = !isToggle || !hasRole(["Applicant"]);
    $[2] = hasRole;
    $[3] = isToggle;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const canEdit = t2;
  const isComment = field === "comment";
  let t3;
  if ($[5] !== field || $[6] !== isComment || $[7] !== row.assignment || $[8] !== row.comment) {
    t3 = () => isComment ? row.comment ?? "" : field === "assignment" ? row.assignment ?? "" : "";
    $[5] = field;
    $[6] = isComment;
    $[7] = row.assignment;
    $[8] = row.comment;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const [text, setText] = useState(t3);
  const [toDo, setToDo] = useState(row.to_do);
  const [coordinates, setCoordinates] = useState(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const toDoRef = useRef(null);
  const submittingRef = useRef(false);
  const {
    mutate: updatePayment,
    isPending
  } = useUpdateMissingCardPayment();
  let t4;
  if ($[10] !== anchorRef || $[11] !== onClose) {
    t4 = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) {
        return;
      }
      if (!document.contains(anchor)) {
        onClose();
        return;
      }
      const anchorRect = anchor.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      let top = anchorRect.bottom + PANEL_MARGIN;
      if (top + panelRect.height > viewportHeight - PANEL_MARGIN) {
        const above = anchorRect.top - PANEL_MARGIN - panelRect.height;
        top = above >= PANEL_MARGIN ? above : Math.max(PANEL_MARGIN, viewportHeight - panelRect.height - PANEL_MARGIN);
      }
      let left = anchorRect.left + anchorRect.width / 2 > viewportWidth / 2 ? anchorRect.right - panelRect.width : anchorRect.left;
      if (left + panelRect.width > viewportWidth - PANEL_MARGIN) {
        left = viewportWidth - panelRect.width - PANEL_MARGIN;
      }
      left = Math.max(PANEL_MARGIN, left);
      setCoordinates({
        top,
        left
      });
    };
    $[10] = anchorRef;
    $[11] = onClose;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const updatePosition = t4;
  let t5;
  let t6;
  if ($[13] !== updatePosition) {
    t5 = () => {
      updatePosition();
    };
    t6 = [updatePosition];
    $[13] = updatePosition;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useLayoutEffect(t5, t6);
  let t7;
  let t8;
  if ($[16] !== updatePosition) {
    t7 = () => {
      const handleReposition = () => updatePosition();
      window.addEventListener("scroll", handleReposition, true);
      window.addEventListener("resize", handleReposition);
      return () => {
        window.removeEventListener("scroll", handleReposition, true);
        window.removeEventListener("resize", handleReposition);
      };
    };
    t8 = [updatePosition];
    $[16] = updatePosition;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[19] !== isComment || $[20] !== isToggle) {
    t9 = () => {
      if (isToggle) {
        toDoRef.current?.focus();
        return;
      }
      const element = isComment ? textareaRef.current : inputRef.current;
      element?.focus();
      element?.select();
    };
    t10 = [isToggle, isComment];
    $[19] = isComment;
    $[20] = isToggle;
    $[21] = t10;
    $[22] = t9;
  } else {
    t10 = $[21];
    t9 = $[22];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[23] !== isPending || $[24] !== onClose) {
    t11 = () => {
      const handlePointerDown = event => {
        if (isPending) {
          return;
        }
        const panel_0 = panelRef.current;
        const target = event.target;
        if (panel_0 && target instanceof Node && !panel_0.contains(target)) {
          onClose();
        }
      };
      const handleKeyDown = event_0 => {
        if (event_0.key === "Escape" && !isPending) {
          event_0.preventDefault();
          onClose();
        }
      };
      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("keydown", handleKeyDown, true);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown, true);
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    };
    t12 = [isPending, onClose];
    $[23] = isPending;
    $[24] = onClose;
    $[25] = t11;
    $[26] = t12;
  } else {
    t11 = $[25];
    t12 = $[26];
  }
  useEffect(t11, t12);
  let t13;
  if ($[27] !== field || $[28] !== row.assignment || $[29] !== row.comment || $[30] !== row.to_do || $[31] !== text || $[32] !== toDo) {
    t13 = () => {
      if (field === "to_do") {
        return toDo !== row.to_do ? {
          to_do: toDo
        } : null;
      }
      const next = text.trim() === "" ? null : text.trim();
      if (field === "assignment") {
        return next === (row.assignment ?? null) ? null : {
          assignment: next
        };
      }
      return next === (row.comment ?? null) ? null : {
        comment: next
      };
    };
    $[27] = field;
    $[28] = row.assignment;
    $[29] = row.comment;
    $[30] = row.to_do;
    $[31] = text;
    $[32] = toDo;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const buildAttributes = t13;
  let t14;
  if ($[34] !== buildAttributes || $[35] !== canEdit || $[36] !== isPending || $[37] !== onClose || $[38] !== row.id || $[39] !== t || $[40] !== updatePayment) {
    t14 = () => {
      if (submittingRef.current || isPending) {
        return;
      }
      if (!canEdit) {
        addToast({
          title: t("paymentsReceived.missingCardPayments.edit.forbidden"),
          color: "danger"
        });
        onClose();
        return;
      }
      const attributes = buildAttributes();
      if (attributes === null) {
        onClose();
        return;
      }
      submittingRef.current = true;
      updatePayment({
        id: row.id,
        attributes
      }, {
        onSuccess: () => {
          addToast({
            title: t("paymentsReceived.missingCardPayments.edit.saveSuccess"),
            color: "success"
          });
          onClose();
        },
        onError: error => {
          submittingRef.current = false;
          addToast({
            title: getEditErrorMessage(error, t, "paymentsReceived.missingCardPayments.edit"),
            color: "danger"
          });
        }
      });
    };
    $[34] = buildAttributes;
    $[35] = canEdit;
    $[36] = isPending;
    $[37] = onClose;
    $[38] = row.id;
    $[39] = t;
    $[40] = updatePayment;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const handleSave = t14;
  let t15;
  if ($[42] !== handleSave) {
    t15 = event_1 => {
      if (event_1.key === "Enter") {
        event_1.preventDefault();
        handleSave();
      }
    };
    $[42] = handleSave;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const handleInputKeyDown = t15;
  let t16;
  if ($[44] !== handleSave) {
    t16 = event_2 => {
      if (event_2.key === "Enter" && (event_2.ctrlKey || event_2.metaKey)) {
        event_2.preventDefault();
        handleSave();
      }
    };
    $[44] = handleSave;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const handleTextareaKeyDown = t16;
  let t17;
  if ($[46] !== handleSave) {
    t17 = event_3 => {
      if (event_3.key === "Enter") {
        event_3.preventDefault();
        handleSave();
      }
    };
    $[46] = handleSave;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const handleToggleKeyDown = t17;
  let t18;
  if ($[48] !== field || $[49] !== label) {
    t18 = label(field);
    $[48] = field;
    $[49] = label;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const t19 = `fixed z-50 flex flex-col gap-3 rounded-xl border border-border bg-content1 p-4 shadow-xl dark:border-[#3a3a3a] ${isComment ? "w-96" : "w-72"}`;
  const t20 = coordinates?.top ?? -9999;
  const t21 = coordinates?.left ?? -9999;
  const t22 = coordinates ? "visible" : "hidden";
  let t23;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t22) {
    t23 = {
      top: t20,
      left: t21,
      visibility: t22
    };
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== field || $[56] !== label) {
    t24 = label(field);
    $[55] = field;
    $[56] = label;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t24) {
    t25 = <p className="text-sm font-semibold text-foreground">{t24}</p>;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== canEdit || $[61] !== field || $[62] !== handleInputKeyDown || $[63] !== handleTextareaKeyDown || $[64] !== handleToggleKeyDown || $[65] !== isComment || $[66] !== isPending || $[67] !== isToggle || $[68] !== label || $[69] !== t || $[70] !== text || $[71] !== toDo) {
    t26 = isToggle ? <div className="flex items-center gap-2" onKeyDown={handleToggleKeyDown}><Switch ref={toDoRef} size="sm" isSelected={isDone(toDo)} onValueChange={done => setToDo(!done)} isDisabled={isPending || !canEdit} aria-label={label("to_do")} /><span className="text-sm text-foreground">{isDone(toDo) ? t("paymentsReceived.missingCardPayments.yes") : t("paymentsReceived.missingCardPayments.no")}</span></div> : isComment ? <Textarea ref={textareaRef} minRows={4} maxRows={10} value={text} onValueChange={setText} onKeyDown={handleTextareaKeyDown} isDisabled={isPending} aria-label={label("comment")} /> : <Input ref={inputRef} size="sm" value={text} onValueChange={setText} onKeyDown={handleInputKeyDown} isDisabled={isPending} aria-label={label(field)} />;
    $[60] = canEdit;
    $[61] = field;
    $[62] = handleInputKeyDown;
    $[63] = handleTextareaKeyDown;
    $[64] = handleToggleKeyDown;
    $[65] = isComment;
    $[66] = isPending;
    $[67] = isToggle;
    $[68] = label;
    $[69] = t;
    $[70] = text;
    $[71] = toDo;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <IconX size={16} />;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== t) {
    t28 = t("paymentsReceived.missingCardPayments.edit.cancel");
    $[74] = t;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== t) {
    t29 = t("paymentsReceived.missingCardPayments.edit.cancel");
    $[76] = t;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== isPending || $[79] !== onClose || $[80] !== t28 || $[81] !== t29) {
    t30 = <Button size="sm" variant="light" onClick={onClose} isDisabled={isPending} startContent={t27} aria-label={t28}>{t29}</Button>;
    $[78] = isPending;
    $[79] = onClose;
    $[80] = t28;
    $[81] = t29;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  const t31 = !canEdit;
  let t32;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <IconCheck size={16} />;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== t) {
    t33 = t("paymentsReceived.missingCardPayments.edit.validate");
    $[84] = t;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== t) {
    t34 = t("paymentsReceived.missingCardPayments.edit.validate");
    $[86] = t;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== handleSave || $[89] !== isPending || $[90] !== t31 || $[91] !== t33 || $[92] !== t34) {
    t35 = <Button size="sm" color="primary" onClick={handleSave} isLoading={isPending} isDisabled={t31} startContent={t32} aria-label={t33}>{t34}</Button>;
    $[88] = handleSave;
    $[89] = isPending;
    $[90] = t31;
    $[91] = t33;
    $[92] = t34;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  let t36;
  if ($[94] !== t30 || $[95] !== t35) {
    t36 = <div className="flex justify-end gap-2">{t30}{t35}</div>;
    $[94] = t30;
    $[95] = t35;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  let t37;
  if ($[97] !== t18 || $[98] !== t19 || $[99] !== t23 || $[100] !== t25 || $[101] !== t26 || $[102] !== t36) {
    t37 = createPortal(<div ref={panelRef} role="dialog" aria-modal="false" aria-label={t18} className={t19} style={t23}>{t25}{t26}{t36}</div>, document.body);
    $[97] = t18;
    $[98] = t19;
    $[99] = t23;
    $[100] = t25;
    $[101] = t26;
    $[102] = t36;
    $[103] = t37;
  } else {
    t37 = $[103];
  }
  return t37;
};